<template>
  <div class="layout" :class="{ 'layout--bottom-nav': profileStore.isAuthenticated }">
    <NotificationsPortal />
    <ForcedNoticeModal v-if="profileStore.profile" />
    <AppHeader />
    <VpnTabNav />

    <PullToRefresh :disabled="layoutStore.pullToRefreshDisabled">
      <main class="layout__main" :class="{ 'layout__main--loading': isNavigating }">
        <RouterView v-slot="{ Component }">
          <Transition name="page-fade" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
        
        <!-- Переход между разделами: тонкая полоса сверху вместо заливки
             во весь экран. Полоса появляется только если переход затянулся
             (см. showNavigationBar) — на готовом чанке человек её не увидит. -->
        <Transition name="fade">
          <div
            v-if="showNavigationBar"
            class="navigation-bar"
            role="progressbar"
            :aria-label="$t('common.loading')"
          >
            <div class="navigation-bar__fill" />
          </div>
        </Transition>
      </main>
    </PullToRefresh>

    <AppFooter />
    <AppBottomNav />

    <!-- Intercom Support Widget (logic only, safe to catch inside layout) -->
    <IntercomWidget />

    <!-- Connection Error Modal (blocking) -->
    <ConnectionErrorModal :visible="showConnectionError" />

    <!-- Telegram Mini-App: TOTP Verification Modal -->
    <TotpModal
      v-if="isInTelegram"
      :visible="tmaState?.status === 'totp_required'"
      :spa-auth-token="tmaState?.authResult?.spa_auth_token ?? ''"
      @success="handleTmaTokenExchange"
    />
  </div>
</template>


<script setup lang="ts">
import { ref, inject, watch, onMounted, onUnmounted, computed } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { NotificationsPortal } from '@space/notifications'

import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import VpnTabNav from '@/components/navigation/VpnTabNav.vue'
import AppBottomNav from '@/components/navigation/AppBottomNav.vue'
import IntercomWidget from '@/components/common/IntercomWidget.vue'
import ConnectionErrorModal from '@/components/common/ConnectionErrorModal.vue'
import PullToRefresh from '@/components/common/PullToRefresh.vue'
import { TotpModal } from '@/mini-app'
import type { TmaState } from '@/mini-app'
import { useAppInitialization } from '@/shared/composables/useAppInitialization'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useNotificationsStore } from '@/modules/notifications/store/notificationsStore'
import ForcedNoticeModal from '@/modules/notifications/components/ForcedNoticeModal.vue'
import { useLayoutStore } from '@/store/layoutStore'
import { prefetchNavigationChunks } from '@/router/prefetchNavigation'
import { spaceSocket } from '@/services/spaceSocket'
import iziToast from 'izitoast'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { initializeApp } = useAppInitialization()
const profileStore = useProfileStore()
const notificationsStore = useNotificationsStore()
const layoutStore = useLayoutStore()

watch(() => profileStore.profile?.id, (id, previous) => {
  if (id === previous) return
  notificationsStore.reset()
  if (id) notificationsStore.startPolling()
}, { immediate: true })

const showConnectionError = ref(false)
const isNavigating = computed(() => layoutStore.isNavigating)

/**
 * Индикатор перехода ждёт четверть секунды, прежде чем показаться. Чанк из
 * кэша приезжает быстрее, и мигание полосы на каждом нажатии вкладки читается
 * как подтормаживание там, где ничего не тормозит.
 */
const showNavigationBar = ref(false)
let navigationBarTimer: number | undefined

watch(isNavigating, (navigating) => {
  window.clearTimeout(navigationBarTimer)

  if (!navigating) {
    showNavigationBar.value = false
    return
  }

  navigationBarTimer = window.setTimeout(() => {
    showNavigationBar.value = true
  }, 250)
})

// Telegram Mini-App state (provided in main.ts)
const tmaState = inject<Ref<TmaState>>('tmaState')
const isInTelegram = inject<boolean>('isInTelegram', false)

let disposed = false
let tokenExchangePending = false

/**
 * Handle success from TotpModal:
 * exchange the one-time token for a session, store it.
 * Setting tmaState to 'done' triggers TmaInitPage watcher → fetchProfile → redirect.
 */
async function handleTmaTokenExchange(spaAuthToken: string) {
  if (disposed || tokenExchangePending || tmaState?.value.status !== 'totp_required') return
  if (typeof spaAuthToken !== 'string' || !spaAuthToken.trim()) {
    console.error('[TMA] handleTmaTokenExchange called without spaAuthToken')
    if (tmaState) {
      tmaState.value.status = 'error'
      tmaState.value.errorMessage = 'No auth token received'
    }
    return
  }

  tokenExchangePending = true
  const initData = tmaState.value.initData
  const current = () => !disposed && tmaState.value.initData === initData && tmaState.value.status === 'totp_required'
  try {
    const { TelegramAuthService } = await import('@/mini-app/TelegramAuthService')
    if (!current()) return
    const token = await TelegramAuthService.exchangeToken(spaAuthToken)
    if (!current()) return
    if (typeof token !== 'string' || !token.trim()) throw new Error(t('errors.loginFailed'))

    const { createTokenStorage } = await import('@space/shared-api-client')
    if (!current()) return
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
    const tokenStorage = createTokenStorage({ storageKey })
    tokenStorage.setToken(token)

    if (tmaState) {
      tmaState.value.status = 'done'
    }
    // TmaInitPage watcher picks up status='done' → fetchProfile → router.replace('/dashboard')
  } catch (e: unknown) {
    if (current()) {
      tmaState.value.status = 'error'
      tmaState.value.errorMessage = e instanceof Error ? e.message : 'Token exchange failed'
    }
  } finally {
    tokenExchangePending = false
  }
}

// Connection error event handler
function onConnectionError() {
  showConnectionError.value = true
}

// Watch for profile loading: initialize WebSocket & show country modal if needed
watch(
  () => profileStore.profile,
  (profile) => {
    if (!profile) return
    
    // Initialize WebSocket connection when ws config is available
    if (profile.ws) {
      spaceSocket.init(profile.ws)

      // Subscribe to global broadcast channel (toast notifications)
      spaceSocket.subscribe('global', (data: any) => {
        // Живой канал только ускоряет обновление колокольчика: пришло
        // событие — перечитываем список, не дожидаясь очередного опроса.
        // Если канал не поднимется, кабинет продолжит жить на опросе
        // (см. startPolling в modules/notifications/store).
        void notificationsStore.load()

        let payload: any
        try {
          payload = typeof data === 'object' ? data : JSON.parse(data)
        } catch {
          // Кривой payload — это не повод ронять обработчик канала.
          return
        }

        // Handle redirect action
        if (payload.action === 'redirect' && payload.url) {
          window.location.href = payload.url
          return
        }

        // Show toast notification
        if (payload.title) {
          iziToast.info({
            title: payload.title,
            message: payload.text || '',
            position: 'topRight',
            timeout: 7000,
            ...(payload.image ? { imageWidth: 56, image: payload.image } : {}),
          })
        }
      })
    }
  },
  { immediate: true }
)

// Start profile loading in background - pages render their skeletons immediately
onMounted(() => {
  // KeyLoginPage validates the saved session without losing the checkout URL on 401.
  if (route.name !== 'auth-key') initializeApp()
  window.addEventListener('gonkagame:connection-error', onConnectionError)
})

/**
 * Чанки остальных разделов тянем фоном — но только вошедшему: гостю на
 * лендинге кабинет не нужен, и качать его в чужой трафик незачем.
 */
watch(
  () => profileStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) prefetchNavigationChunks(router)
  },
  { immediate: true },
)

onUnmounted(() => {
  disposed = true
  window.clearTimeout(navigationBarTimer)
  window.removeEventListener('gonkagame:connection-error', onConnectionError)
  spaceSocket.disconnect()
})
</script>

<style scoped lang="scss">
.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  // Removed height: 100vh and overflow-y: auto
  // Let body manage scroll to prevent sticky header + backdrop-filter repaint conflicts
  background: var(--hm-bg-base);
  // Шапка (AppHeader) закреплена и выпала из потока — резервируем под неё место.
  padding-top: var(--app-header-height);

  // На телефоне внизу закреплено меню (AppBottomNav) — контент и подвал
  // не должны под него заезжать.
  &--bottom-nav {
    @media (max-width: 768px) {
      padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
    }
  }

  &__main {
    flex: 1;
    padding: 10px 20px 40px;
    position: relative;
    // App content canvas: recessed grey (light) / deepest violet (dark) so the
    // white/violet panels on top separate by contrast and the canvas fills the
    // whole screen edge-to-edge (no white gutter around the page).
    background: var(--ds-surface-sunken);
    
    @media (max-width: 768px) {
      // IGRA-20: no more fixed bottom nav (replaced by VpnTabNav, in normal
      // flow under the header) — content no longer needs to clear it.
      padding: 0px 10px 24px;
    }
    
    &--loading {
      pointer-events: none;
    }
  }
}

// Переход между разделами показывает полоса вверху страницы, а не заливка
// во весь экран. Раньше здесь был `.navigation-overlay`: position fixed,
// inset 0, непрозрачный градиент поверх шапки и меню — при каждом нажатии
// вкладки кабинет на время загрузки чанка пропадал целиком.
.navigation-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 100;
  background: var(--ds-accent-soft-20);
  overflow: hidden;
}

.navigation-bar__fill {
  width: 40%;
  height: 100%;
  background: var(--ds-accent);
  animation: nav-bar-slide 1.1s ease-in-out infinite;
}

@keyframes nav-bar-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}


// Page fade transition
.page-fade-enter-active {
  transition: opacity 0.15s ease-out;
}

.page-fade-leave-active {
  transition: opacity 0.1s ease-in;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}

// Overlay fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

